import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { AuthProvider, useAuth } from './context/AuthContext'
import Layout from './components/layout/Layout'
import ProtegerRuta from './components/permisos/ProtegerRuta'

// Pages (carga inmediata - criticas)
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import Voluntarios from './pages/Voluntarios'

// Pages (lazy loading) - Módulo de Afectados
const AfectadosDashboard = lazy(() => import('./pages/Afectados/Dashboard'))
const AfectadosLista = lazy(() => import('./pages/Afectados/Lista'))
const AfectadosRegistro = lazy(() => import('./pages/Afectados/Registro'))
const AfectadosConfig = lazy(() => import('./pages/Afectados/Configuracion'))
const AfectadosSolicitudes = lazy(() => import('./pages/Afectados/Solicitudes'))
const AfectadosSeguimiento = lazy(() => import('./pages/Afectados/Seguimiento'))
const AfectadosDetalle = lazy(() => import('./pages/Afectados/Detalle'))
const Mapa = lazy(() => import('./pages/Mapa'))
const Reportes = lazy(() => import('./pages/Reportes'))
const Perfil = lazy(() => import('./pages/Perfil'))
const RegistroPublico = lazy(() => import('./pages/Afectados/RegistroPublico'))

// Inventario
const DashboardInventario = lazy(() => import('./pages/inventario/DashboardInventario'))
const CentrosAcopio = lazy(() => import('./pages/inventario/CentrosAcopio'))
const Categorias = lazy(() => import('./pages/inventario/Categorias'))
const Insumos = lazy(() => import('./pages/inventario/Insumos'))
const Kits = lazy(() => import('./pages/inventario/Kits'))
const Donaciones = lazy(() => import('./pages/inventario/Donaciones'))
const Ingresos = lazy(() => import('./pages/inventario/Ingresos'))
const Salidas = lazy(() => import('./pages/inventario/Salidas'))
const Entregas = lazy(() => import('./pages/inventario/Entregas'))
const Traslados = lazy(() => import('./pages/inventario/Traslados'))
const Ajustes = lazy(() => import('./pages/inventario/Ajustes'))
const Existencias = lazy(() => import('./pages/inventario/reportes/Existencias'))
const Kardex = lazy(() => import('./pages/inventario/reportes/Kardex'))
const Permisos = lazy(() => import('./pages/Configuracion/Permisos'))

// Rutas de entrega
const Rutas = lazy(() => import('./pages/Rutas'))
const DetalleRuta = lazy(() => import('./pages/Rutas/DetalleRuta'))

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth()
  if (loading) return <div className="flex justify-center items-center h-screen">Cargando...</div>
  return user ? children : <Navigate to="/login" />
}

const LazyPage = ({ Component }) => (
  <Suspense fallback={<div className="flex justify-center items-center h-64"><div className="w-8 h-8 border-3 border-gray-200 border-t-red-600 rounded-full animate-spin" /></div>}>
    <Component />
  </Suspense>
)

const RutaProtegida = ({ modulo, permiso = 'ver', Component }) => (
  <PrivateRoute>
    <ProtegerRuta modulo={modulo} permiso={permiso}>
      <LazyPage Component={Component} />
    </ProtegerRuta>
  </PrivateRoute>
)

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/registro-publico" element={<LazyPage Component={RegistroPublico} />} />
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Layout />}>
        <Route index element={<PrivateRoute><ProtegerRuta modulo="dashboard"><Dashboard /></ProtegerRuta></PrivateRoute>} />
        <Route path="afectados" element={<RutaProtegida modulo="afectados" Component={AfectadosDashboard} />} />
        <Route path="afectados/lista" element={<RutaProtegida modulo="afectados" Component={AfectadosLista} />} />
        <Route path="afectados/registro" element={<RutaProtegida modulo="afectados" Component={AfectadosRegistro} />} />
        <Route path="afectados/configuracion" element={<RutaProtegida modulo="configuracion" Component={AfectadosConfig} />} />
        <Route path="afectados/solicitudes" element={<RutaProtegida modulo="afectados" Component={AfectadosSolicitudes} />} />
        <Route path="afectados/seguimiento" element={<RutaProtegida modulo="afectados" Component={AfectadosSeguimiento} />} />
        <Route path="afectados/:id" element={<RutaProtegida modulo="afectados" Component={AfectadosDetalle} />} />
        <Route path="voluntarios" element={<PrivateRoute><ProtegerRuta modulo="voluntarios"><Voluntarios /></ProtegerRuta></PrivateRoute>} />
        <Route path="perfil" element={<PrivateRoute><LazyPage Component={Perfil} /></PrivateRoute>} />

        {/* Rutas de entrega */}
        <Route path="rutas" element={<RutaProtegida modulo="rutas" Component={Rutas} />} />
        <Route path="rutas/:id" element={<RutaProtegida modulo="rutas" Component={DetalleRuta} />} />

        {/* Inventario */}
        <Route path="inventario" element={<RutaProtegida modulo="inventario" Component={DashboardInventario} />} />
        <Route path="inventario/centros" element={<RutaProtegida modulo="inventario_centros" Component={CentrosAcopio} />} />
        <Route path="inventario/categorias" element={<RutaProtegida modulo="inventario_categorias" Component={Categorias} />} />
        <Route path="inventario/insumos" element={<RutaProtegida modulo="inventario_insumos" Component={Insumos} />} />
        <Route path="inventario/kits" element={<RutaProtegida modulo="inventario_insumos" Component={Kits} />} />
        <Route path="inventario/donaciones" element={<RutaProtegida modulo="inventario_donaciones" Component={Donaciones} />} />
        <Route path="inventario/ingresos" element={<RutaProtegida modulo="inventario_ingresos" Component={Ingresos} />} />
        <Route path="inventario/salidas" element={<RutaProtegida modulo="inventario_salidas" Component={Salidas} />} />
        <Route path="inventario/entregas" element={<RutaProtegida modulo="entregas" Component={Entregas} />} />
        <Route path="inventario/traslados" element={<RutaProtegida modulo="inventario_traslados" Component={Traslados} />} />
        <Route path="inventario/ajustes" element={<RutaProtegida modulo="inventario_ajustes" Component={Ajustes} />} />
        <Route path="inventario/existencias" element={<RutaProtegida modulo="inventario_existencias" Component={Existencias} />} />
        <Route path="inventario/kardex" element={<RutaProtegida modulo="inventario_kardex" Component={Kardex} />} />
        <Route path="configuracion/permisos" element={<RutaProtegida modulo="permisos" Component={Permisos} />} />
      </Route>
    </Routes>
  )
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  )
}

export default App